import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { RecordContextProvider, useGetOne, useRecordContext } from "react-admin";
import { grants } from "../backoffice.access_control";
import { PartialRefundPaymentDialogButton } from "../buttons/PartialRefundInvoiceDialogButton";
import { OpenInStripeIconButton } from "../buttons/OpenInStripeIconButton";
import { RetryPaymentButton } from "../buttons/RetryPaymentButton";
import { Invoice } from "../providers/invoicesProvider";
import type { Student } from "../providers/studentsProvider.js";
import { UpdateAddressOnPDFButton } from "../buttons/UpdateAddressOnPDFButton";
import { Row } from "../misc/Row";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Column } from "../misc/Column";
import { getInvoiceBrief, getInvoiceDateAndTime } from "../lists/InvoicesList";
import { DialogTopRightActions } from "../misc/DialogTopRightActions";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { MoneyField } from "../fields/MoneyField";
import { BookedTrainingsField } from "../details/student/BookedTrainingsField";
import { updateInvoice } from "../api/backend.api";
import { DialogProps } from "../misc/DialogProps";

export function StudentInvoiceDialog(props: { student: Student; invoiceId: string; dialogProps: DialogProps }) {
  const { student, invoiceId, dialogProps } = props;
  const { data: invoice, isLoading } = useGetOne<Invoice>("b2cInvoices", { id: invoiceId });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  if (isLoading || !invoice) {
    return null;
  }
  const { date, time } = getInvoiceDateAndTime(invoice);

  const updateInvoiceBookedTrainings = async (bookedTrainingIds: string[]) => {
    await updateInvoice(invoice.payload.id, { booked_training_id: bookedTrainingIds });
  };

  return (
    <Dialog
      {...dialogProps}
      maxWidth="lg"
      fullScreen={isMobile}
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: "8px" } } }}
    >
      <DialogTitle>Rechnung</DialogTitle>
      <DialogCloseButton onClick={dialogProps.onClose} />
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <RecordContextProvider value={invoice}>
          <_DialogActions student={student} />
          <Grid2 container spacing={{ xs: 2, sm: 1 }} mt={1}>
            <_InvoiceDetailsItem label="Leistung">
              <Column>
                {invoice.payload.positions.map((p) => (
                  <Typography variant="body2" key={p.id}>
                    {getInvoiceBrief(p)}
                  </Typography>
                ))}
              </Column>
            </_InvoiceDetailsItem>
            <_InvoiceDetailsItem label="Offen">
              <MoneyField source="payment_amount" />
            </_InvoiceDetailsItem>
            <_InvoiceDetailsItem label="Leistungszeitpunkt">
              <Typography variant="body2">
                {date} {time} Uhr
              </Typography>
            </_InvoiceDetailsItem>
            <_InvoiceDetailsItem label="Erstattet">
              <MoneyField source="refunded_amount" />
            </_InvoiceDetailsItem>
            <_InvoiceDetailsItem label="Führerscheinklasse">
              <BookedTrainingsField
                student={student}
                bookedTrainingIds={invoice.bookedTrainingIds}
                handleSubmit={updateInvoiceBookedTrainings}
              />
            </_InvoiceDetailsItem>
          </Grid2>
          <Column mt={2} className="invoice-pdf" sx={{ "& iframe": { minHeight: { xs: "400px", sm: "600px" } } }}>
            {invoice.downloadUrl && (
              <Box
                component="iframe"
                sx={{ width: "100%", border: 1, borderColor: "divider" }}
                src={invoice.downloadUrl}
              />
            )}
          </Column>
        </RecordContextProvider>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 1 } }}>
        <Row
          gap={2}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
            "& > button": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          {grants.has("Invoice:refund") && <PartialRefundPaymentDialogButton invoice={invoice.payload} />}
          {grants.has("updateInvoicePDF") && <UpdateAddressOnPDFButton invoice={invoice.payload} />}
        </Row>
      </DialogActions>
    </Dialog>
  );
}

function _InvoiceDetailsItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Grid2 xs={12} sm={6} sx={{ minWidth: 0 }}>
      <Row
        gap={{ xs: 0.5, sm: 1 }}
        sx={{ alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, minWidth: 0 }}
      >
        <Typography variant="body1" fontWeight={500}>
          {label}:
        </Typography>
        <Row gap={0.5} sx={{ alignItems: "center", minWidth: 0 }}>
          {children}
        </Row>
      </Row>
    </Grid2>
  );
}

function _DialogActions({ student }: { student: Student }) {
  const invoice = useRecordContext<Invoice>();
  return (
    <DialogTopRightActions sx={{ right: { xs: "65px", sm: "54px" } }}>
      {grants.has("retryPayments") && (
        <RetryPaymentButton
          invoiceState={invoice.payload.state}
          invoiceId={invoice.payload.id}
          invoicePaymentMode={invoice.payload.payment_mode}
          student={student}
          paymentAmount={invoice.payload.payment_amount}
          description=""
        />
      )}
      {grants.has("viewStripe") && <OpenInStripeIconButton source="id" />}
      {invoice.downloadUrl && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            open(invoice.downloadUrl!, "_blank");
          }}
          title="Rechnung anzeigen"
        >
          <OpenInNewIcon />
        </IconButton>
      )}
    </DialogTopRightActions>
  );
}
